import { useEffect, useMemo, useState } from 'react';
import { accountSettingsService } from '@/services/account-settings-service';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from 'prosci-ui';
import {
  ProximaApiKeysTab,
  type ApiKeysVariantConfig,
  type CreateDialogProps,
} from './proxima-api-keys-tab';

function ProjectCreateDialog({
  open,
  onClose,
  onCreate,
  isCreating,
  ownerProjects = [],
  ownerProjectsLoading = false,
  takenProjectIds = [],
}: CreateDialogProps) {
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [keyName, setKeyName] = useState('');
  const [externalId, setExternalId] = useState('');

  const availableProjects = useMemo(
    () => ownerProjects.filter((p) => !takenProjectIds.includes(p.id)),
    [ownerProjects, takenProjectIds]
  );

  useEffect(() => {
    if (!open) {
      setSelectedProjectId('');
      setKeyName('');
      setExternalId('');
    }
  }, [open]);

  const handleCreate = () => {
    if (!selectedProjectId) {
      toast.error('Please select a project');
      return;
    }
    if (!keyName.trim()) {
      toast.error('Please enter a name for the project key');
      return;
    }
    const label =
      availableProjects.find((p) => String(p.id) === selectedProjectId)?.name ?? 'Project key';
    onCreate(
      {
        project_id: Number(selectedProjectId),
        name: keyName.trim(),
        external_id: externalId.trim() || null,
      },
      label
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Key</DialogTitle>
          <DialogDescription>
            Project-specific API keys make it easy to manage integrations at a more granular level.
            Add a key to start connecting services to this project.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field>
            <FieldLabel>
              Project Name<span className="text-destructive">*</span>
            </FieldLabel>
            <Select
              value={selectedProjectId}
              onValueChange={setSelectedProjectId}
              disabled={ownerProjectsLoading || (!ownerProjectsLoading && availableProjects.length === 0)}
            >
              <SelectTrigger className="mt-2">
                <SelectValue
                  placeholder={
                    ownerProjectsLoading
                      ? 'Loading projects…'
                      : availableProjects.length === 0
                        ? 'No projects available for selection'
                        : 'Select a project'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {availableProjects.map((project) => (
                  <SelectItem key={project.id} value={String(project.id)}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel>Name<span className="text-destructive">*</span></FieldLabel>
            <Input
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              className="mt-2"
              placeholder="e.g. My Project Key"
            />
          </Field>
          <Field>
            <FieldLabel>External ID</FieldLabel>
            <Input
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              className="mt-2"
              placeholder="e.g. power-bi integration"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isCreating || ownerProjectsLoading}
            className="bg-teal-900"
          >
            {isCreating ? 'Generating…' : 'Generate API Key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PROJECT_CONFIG: ApiKeysVariantConfig = {
  keyType: 'project',
  emptyTitle: 'No project keys added yet',
  emptyDescription:
    'Project API keys allow specific projects within your organization to integrate with external services. Add a key to get started.',
  addButtonLabel: 'Add Project Key',
  sectionTitle: 'Proxima Project API Keys',
  sectionDescription:
    'Create a new project API key for a specific integration service. Once created, the key will be masked for security.',
  tableHeader: 'Key name',
  toastCreated: 'Project API key created successfully',
  toastCreateError: 'Failed to create project API key',
  toastRevoked: 'Project API key revoked',
  toastRevokeError: 'Failed to revoke project API key',
  toastBulkRevoked: 'Selected project API keys revoked',
  toastBulkRevokeError: 'Failed to revoke one or more project keys',
  CreateDialog: ProjectCreateDialog,
  showProjectColumn: true,
  fetchOwnerProjects: (organizationId) =>
    accountSettingsService.getOwnerProjects(String(organizationId)),
};

export function ProximaProjectApiKeysTab() {
  return <ProximaApiKeysTab config={PROJECT_CONFIG} />;
}
